import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Maximize, Volume2 } from "lucide-react";
import { api, fmtTime, imgUrl } from "@/lib/api";
import { beep } from "@/pages/Delivery";
import { cn } from "@/lib/utils";

function Clock() {
  const [n, setN] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setN(new Date()), 10000); return () => clearInterval(t); }, []);
  return <span className="font-mono text-2xl text-slate-300" data-testid="call-panel-clock">{n.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>;
}

function useCalls(slug) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState(false);
  const sound = useRef(false);
  const last = useRef(null);
  useEffect(() => {
    document.documentElement.classList.add("dark");
    const load = () => api.get(`/public/calls/${slug}`).then((r) => {
      const key = r.data.current ? `${r.data.current.code}-${r.data.current.called_at}` : "";
      if (last.current !== null && key && key !== last.current) {
        setFlash(true);
        if (sound.current) beep();
        setTimeout(() => setFlash(false), 5000);
      }
      last.current = key;
      setD(r.data);
      setErr("");
    }).catch((e) => setErr(e.response?.data?.detail || "Painel indisponível no momento"));
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [slug]);
  return { d, err, flash, sound };
}

function Controls({ sound }) {
  const [on, setOn] = useState(false);
  const [full, setFull] = useState(false);
  useEffect(() => { const h = () => setFull(!!document.fullscreenElement); document.addEventListener("fullscreenchange", h); return () => document.removeEventListener("fullscreenchange", h); }, []);
  const btn = "flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 text-sm text-slate-200 hover:bg-white/10 transition-colors";
  return (
    <div className="flex gap-3">
      {!on && <button onClick={() => { sound.current = true; setOn(true); beep(); }} className={btn} data-testid="call-panel-enable-sound"><Volume2 className="h-4 w-4" />Ativar alerta sonoro</button>}
      {!full && <button onClick={() => document.documentElement.requestFullscreen?.()} className={btn} data-testid="call-panel-fullscreen"><Maximize className="h-4 w-4" />Tela cheia</button>}
    </div>
  );
}

export default function CallPanel() {
  const { slug } = useParams();
  const { d, err, flash, sound } = useCalls(slug);
  if (!d) return <div className="grid min-h-screen place-items-center bg-[#07090f] text-slate-400" data-testid="call-panel-loading">{err || "Carregando painel…"}</div>;
  const c = d.current;
  return (
    <div className="flex min-h-screen flex-col bg-[#07090f] text-white" data-testid="call-panel">
      <header className="flex items-center justify-between border-b border-white/10 px-8 py-5">
        <div className="flex items-center gap-4">
          {d.logo_url && <img src={imgUrl(d.logo_url)} alt="" className="h-12 w-12 rounded-xl object-cover" />}
          <p className="font-display text-2xl font-bold" data-testid="call-panel-store">{d.store}</p>
        </div>
        <Clock />
      </header>
      <div className="grid flex-1 lg:grid-cols-[2fr_1fr]">
        <main className={cn("flex flex-col items-center justify-center gap-4 p-10 transition-colors duration-700", flash ? "bg-amber-400 text-slate-950" : "bg-[#0c111c]")}>
          <p className={cn("font-mono text-lg uppercase tracking-[0.4em]", flash ? "text-slate-900" : "text-amber-400")}>Senha chamada</p>
          <p key={c ? `${c.code}${c.called_at}` : "none"} className={cn("fade-up font-mono font-extrabold leading-none tabular-nums text-[9rem] sm:text-[14rem] xl:text-[18rem]", flash && "animate-pulse")} data-testid="call-panel-current">{c?.code || "---"}</p>
          {c?.name && <p className="font-display text-4xl font-semibold sm:text-5xl" data-testid="call-panel-current-name">{c.name}</p>}
          <p className={cn("text-xl", flash ? "text-slate-900" : "text-slate-400")}>{c ? "Retire seu pedido no balcão" : "Aguardando chamadas"}</p>
          <div className="mt-8"><Controls sound={sound} /></div>
        </main>
        <aside className="space-y-4 border-l border-white/10 p-8">
          <p className="font-mono text-sm uppercase tracking-[0.3em] text-slate-400">Chamadas anteriores</p>
          {!d.recent.length && <p className="text-slate-500">Nenhuma ainda.</p>}
          {d.recent.map((r) => (
            <div key={`${r.code}${r.called_at}`} className="flex items-center justify-between rounded-2xl bg-white/5 px-6 py-5" data-testid={`call-panel-recent-${r.code}`}>
              <span className="font-mono text-5xl font-bold tabular-nums">{r.code}</span>
              <span className="text-right text-lg text-slate-300">{r.name}<span className="block text-sm text-slate-500">{fmtTime(r.called_at)}</span></span>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}
